import { useCallback, useEffect, useState } from "react";
import { Phone, MessageCircle, Mail, MessageSquare, RotateCcw, MapPinned, CalendarClock, Undo2, CheckCircle2, X } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const STATUS_CLS = {
  NEW: "bg-red-100 text-red-700", CONTACT_PENDING: "bg-amber-50 text-amber-700", CUSTOMER_CONTACTED: "bg-blue-50 text-blue-700",
  REATTEMPT_REQUESTED: "bg-violet-100 text-violet-700", ADDRESS_UPDATED: "bg-cyan-50 text-cyan-700",
  RESCHEDULED: "bg-cyan-50 text-cyan-700", RTO_REQUESTED: "bg-red-100 text-red-700", RESOLVED: "bg-emerald-100 text-emerald-700",
};

const ACTIONS = [
  { key: "call", label: "Call", icon: Phone },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { key: "sms", label: "SMS", icon: MessageSquare },
  { key: "email", label: "Email", icon: Mail },
  { key: "reattempt", label: "Reattempt", icon: RotateCcw },
  { key: "address_update", label: "Fix Address", icon: MapPinned },
  { key: "reschedule", label: "Reschedule", icon: CalendarClock },
  { key: "rto", label: "RTO", icon: Undo2 },
  { key: "resolve", label: "Resolve", icon: CheckCircle2 },
];

export function NdrActions({ c, size = "sm", onDone }) {
  const [modal, setModal] = useState(null);
  const [extra, setExtra] = useState("");
  const act = async (action, note = "") => {
    try {
      const payload = { action, note };
      if (action === "address_update") payload.new_address = extra;
      if (action === "reschedule") payload.date = extra;
      await api.post(`/ndr/${c.case_id}/action`, payload);
      toast.success(`Action '${action}' recorded on ${c.awb}`);
      setModal(null);
      setExtra("");
      onDone?.();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {ACTIONS.map((a) => (
          <button key={a.key} data-testid={`ndr-${a.key}-${c.awb}`}
            onClick={() => (["address_update", "reschedule"].includes(a.key) ? setModal(a.key) : act(a.key))}
            className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors ${a.key === "rto" ? "border-red-200 text-red-600 hover:bg-red-50" : a.key === "resolve" ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <a.icon className="h-3 w-3" />{a.label}
          </button>
        ))}
      </div>
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ndr-action-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-shipany-navy">{modal === "address_update" ? "Correct delivery address" : "Reschedule delivery"}</h3>
              <button onClick={() => setModal(null)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            {modal === "address_update"
              ? <textarea data-testid="ndr-address-input" rows={3} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="New corrected address" className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />
              : <input data-testid="ndr-date-input" type="date" value={extra} onChange={(e) => setExtra(e.target.value)} className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />}
            <button onClick={() => act(modal)} data-testid="ndr-action-confirm-btn" className="mt-4 w-full rounded-xl bg-shipany-blue py-2.5 text-sm font-bold text-white hover:bg-[#0648B3]">Confirm</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function NdrCenter() {
  const [cases, setCases] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => api.get("/ndr", { params: filter ? { status: filter } : {} }).then(({ data }) => setCases(data)).catch(() => {}), [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="ndr-center-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">NDR Center</h1>
          <p className="text-sm text-slate-500">Rescue failed deliveries before they become RTO.</p>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="ndr-filter"
          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-shipany-blue focus:outline-none">
          <option value="">All statuses</option>
          {Object.keys(STATUS_CLS).map((s) => <option key={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      <div className="mt-6 space-y-4" data-testid="ndr-cases-list">
        {(cases || []).map((c) => (
          <div key={c.case_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`ndr-case-${c.awb}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <p className="mono font-bold text-shipany-blue">{c.awb}</p>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${STATUS_CLS[c.status] || "bg-slate-100 text-slate-500"}`}>{c.status.replace(/_/g, " ")}</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{c.customer_name} · {c.customer_phone} · {c.destination_city}</p>
                <p className="mt-0.5 text-xs text-slate-400">{c.courier_name} · Attempt #{c.attempt} · {c.reason} · {fmtDateTime(c.created_at)}</p>
              </div>
            </div>
            <div className="mt-4"><NdrActions c={c} onDone={load} /></div>
            {c.actions?.length > 1 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs font-semibold text-slate-400 hover:text-shipany-blue">Action history ({c.actions.length})</summary>
                <div className="mt-2 space-y-1.5">
                  {c.actions.map((a, i) => (
                    <p key={i} className="mono rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">{a.at?.slice(0, 16).replace("T", " ")} · {a.action} · {a.by}{a.note ? ` — ${a.note}` : ""}</p>
                  ))}
                </div>
              </details>
            )}
          </div>
        ))}
        {cases && cases.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">No NDR cases. Delivery exceptions will appear here automatically.</div>
        )}
      </div>
    </div>
  );
}
